import { apiClient } from "./client";
import {
	getPublicUserId,
	STORAGE_KEYS,
	getUserIdFromStorage,
} from "../../shared-utils";
import { hydrateAppContextFromNativeBridge } from "./appContext";

export interface UserProfile {
	id: string;
	hashedVerifiedEmail?: string;
	isGlobalModerator?: boolean;
	isGlobalAdmin?: boolean;
	emailDomain?: string;
	wildcardEmailDomain?: string;
	// Other fields omitted as not currently needed
}

export async function getAndStoreCurrentUserId(): Promise<string | null> {
	const userId = await getCurrentUserId();
	if (userId) {
		localStorage.setItem(STORAGE_KEYS.USER_ID, userId);
	}
	return userId;
}

/**
 * Get the current user ID
 * Checks window.APP_USER_ID (injected by webview), then localStorage, then API
 */
export async function getCurrentUserId(): Promise<string | null> {
	try {
		// First check window.APP_USER_ID (injected by webview)
		if (typeof window !== "undefined" && window.APP_USER_ID) {
			console.log("[ApiClient] Using APP_USER_ID from window");
			return window.APP_USER_ID;
		}

		// Check environment variable (injected by bun dev/build)
		const envUserId = getPublicUserId();
		if (envUserId) {
			console.log("[ApiClient] Using BUN_PUBLIC_USER_ID from env");
			return envUserId;
		}

		// Fallback to localStorage (including migration from legacy)
		const storedUserId = getUserIdFromStorage();
		if (storedUserId) {
			console.log("[ApiClient] Using userId from storage");
			return storedUserId;
		}

		// If webview globals were late, ask native for full app context once.
		console.log(
			"[ApiClient] APP_USER_ID missing; attempting getAppContext hydration.",
		);
		await hydrateAppContextFromNativeBridge({
			reason: "get_current_user_id",
		});
		if (typeof window !== "undefined" && window.APP_USER_ID) {
			console.log("[ApiClient] Using APP_USER_ID from getAppContext");
			return window.APP_USER_ID;
		}

		const hydratedStoredUserId = getUserIdFromStorage();
		if (hydratedStoredUserId) {
			console.log("[ApiClient] Using hydrated userId from storage");
			return hydratedStoredUserId;
		}

		// Last resort: fetch from API
		const user = await apiClient.get<UserProfile>("/v1/users/me");
		return user.id;
	} catch (error) {
		console.warn("Failed to fetch current user:", error);
		return null;
	}
}

const sidechatInternal = [
	"dc64ecf4-74bf-4bac-96f3-33d0fc3d04c3", // assaf
	"00f0792f-0ee2-4f64-8d31-bc707387d7a7",
	"04fbdf53-708e-4fd7-844d-fdbcc1f21254",
	"08efcdeb-d2d5-4d7c-aa79-bdb8959cc7a8",
	"19276149-99ca-4256-9f70-a69cec6aa004",
	"1b7b1099-32f2-4f5f-a372-cc6125b9dc60", // hari dev
	"1c8acc6d-e0c3-4aa3-bfff-4d1d291e869e",
	"1ce4cf9c-64a5-4325-993b-e2f714b1fd7b",
	"1d62eaaa-bbe4-443e-ac39-e806712e6366",
	"1d63e34f-d871-4e9a-87c9-8c071f3573b2",
	"2c202432-bbb6-469d-a596-646da81bc75c",
	"2f8f22dd-c179-4fcd-a08a-d90766186028",
	"2fbac97d-4228-437a-9f90-096973d01588",
	"39a70bdd-5a90-4e75-9f60-b77ddbafcb74",
	"3c462527-0356-419d-9140-d98cddfcca3b",
	"43b37aa8-953d-495f-933d-a8225f6740bd",
	"63e3b8a9-0647-4ed8-9dd9-4274ed260bad",
	"6456d995-bd13-44fd-a684-0b26f023a2da",
	"663f35d8-be73-4a5a-baae-f3738149a7ee",
	"798caba5-8b21-4b43-9e96-0963b48855bb",
	"842b9af1-79d7-4d17-a7f7-ca2dd82daa9d",
	"84d01484-b04f-4e1c-a771-6796eda5a461",
	"8ce93349-35b5-4b03-93a2-9e06bc4d2f73",
	"a15d557e-d0dc-486c-baa1-9dd67865e8d1",
	"a2e28239-b163-45ac-9e91-1ab1fa472dce",
	"a433c11c-286d-4f44-9827-3abea73b44cc",
	"a69142fa-4293-40e9-9e8f-eab9ffb8e2e3",
	"a77c5c00-31f1-43a0-8d44-a67c337e077b",
	"b28935e9-0721-4df0-a18a-6dca1c55b96d",
	"b29899f0-745d-486e-a277-e8e5ca625e18",
	"be0e38c1-b7bc-4a23-a341-a9865c35b407",
	"bf619d77-5d4b-4965-8265-800cb29f100a",
	"c7003282-9414-4f90-a4c7-5c9776d107b1",
	"d10f5e5d-80b8-491d-aa8a-dccb362c2052",
	"d384196a-df98-4665-9916-55116bf0376e",
	"d70f227f-1c8b-4382-93fc-df0cc4558422",
	"dd63d92d-a184-4301-8c39-7d088210e004", // hari
	"df650c56-e77d-4269-800b-1f37441f5fdb",
	"e48fccfb-be0f-4353-8053-8d9423195728",
	"e76d8398-653a-4e81-84cf-9c70d956f25c",
	"ecac099f-c7ff-49a1-854f-68dafae3e728",
	"f9d5c678-114a-4ae2-8ca4-fbbfa5c2190d",
	"d701ae94-4633-487d-b833-e4cee85335b7", // kyle's  dev simulator - dark pink something
	// 'f2be299a-5443-4e92-9c54-fdd531c0c519', -- autoposterID
];

export const isSidechatInternal = async (userId?: string | null) => {
	const id = userId ?? (await getCurrentUserId());
	if (!id) return false;
	return sidechatInternal.includes(id);
};

const ambassadorUserIds = [
	"0c75db13-bdb9-4af2-a34e-487c08d09d50",
	"182f9c3b-49e8-412e-b78d-d69a384855b0",
	"1bfea2f4-03de-40c4-bf0f-3f748785a1d2",
	"1ce4cf9c-64a5-4325-993b-e2f714b1fd7b",
	"1e0e78ec-ee09-4ae7-a640-cc618f4278da",
	"1ea53cc1-879e-49bd-8273-81376139d02f",
	"1faa294c-6263-465f-8a1f-7ba1c48e5134",
	"26bd1567-045b-4506-b52a-0a00504b2e58",
	"276c9993-8d92-435c-ba20-698fe4e097ff",
	"2db8b793-b966-4a1c-add7-dab92a1d2d73",
	"3247441d-cbf8-4fd4-b65d-f1e20eaeb87f",
	"3a83297e-be58-43b9-9a86-7ac316a2165f",
	"3bed1c74-3da4-4db4-be1a-e887194e2688",
	"404ad501-2199-4fa2-9471-d8e755625a66",
	"4235d0d4-8f08-4f8b-8d6f-711a5da81a29",
	"42f02d91-10c2-44ba-8660-21bdb833a251",
	"4331ddf5-8922-4d0a-ab1d-4f6fbf709bdf",
	"4b011b90-19cd-45f3-8d81-d0b9d223c248",
	"4c12e21d-d24b-44fb-ac30-b35ea196bbe8",
	"4f256909-c65c-4dba-961b-470978e126cc",
	"4fbbd30b-ec26-4cec-9e47-9beda5aa3116",
	"500e21f3-6181-413e-9a2b-65b475b01f3b",
	"522719ac-8f78-4fb9-b20d-3cb0e7f64879",
	"53ef6dae-abf8-41ee-ab15-7d0fda8330a5",
	"5497b3b3-5f5f-4e7d-aebb-b80475a17ff0",
	"55de219d-b679-48d9-bb4c-21570f8f16d2",
	"5d4db518-bcfd-41a6-8a78-b1fbee106b78",
	"5f4c900d-c751-4907-9069-f63b17dd0d8a",
	"61726a67-4163-40ba-a319-42ae3ecded08",
	"61de4266-1b7b-494a-afbc-42e5545b0e67",
	"635351fb-9f38-4c13-a419-24d2ed34cda1",
	"6ac10b47-de25-492b-87b2-93c9227f655b",
	"6f5f1bf5-b3d5-41e1-9014-0a9c72dde62e",
	"771e1055-80f2-45c6-810d-9b3921100345",
	"7a32d80d-8518-4676-9b68-618a829dfe8e",
	"7d583968-aa0b-4a8b-9c8e-68cd43e31f4e",
	"7ea457da-9582-4ba2-af73-6f98681a37ee",
	"80bd5531-02c1-4612-b99d-3138dc4873ca",
	"83a75b6c-17db-4643-8178-c8a5c4f87679",
	"8bac2c26-9f4d-480c-8ea9-e7573de3f46d",
	"8dffff06-b3f6-4d84-afdd-f5da30504be5",
	"8ed870a0-4be6-499c-9546-e4be7b570530",
	"90b061de-49aa-4282-bdf7-2e6015aabb36",
	"922fdd2c-8ba6-48e8-8580-be59fa9138b7",
	"926cecc3-8380-44f0-84b7-22ea69148ed8",
	"931e93b2-6a12-4a95-9c69-e91598c4c3fb",
	"95b7ada7-e68e-4d02-a988-37d77a2bd4c7",
	"9607f843-4d38-47f4-85f6-4b9214bbbe35",
	"995ed3aa-bbe7-4216-acd1-4260e90f1a1f",
	"9e837ea9-73d4-4fe7-a05f-4f8c97547790",
	"9ee9b1cf-bde3-4c9b-befe-ad12c0cf887b",
	"9f707c6e-6c47-4b44-869d-83176132c540",
	"9f9a5450-e04b-418c-8945-003d5f172fef",
	"a10e27ab-59bc-4cbf-87c3-8b3264eea9b2",
	"a606bc0a-0e8b-4a00-96f3-da48d1486684",
	"a69142fa-4293-40e9-9e8f-eab9ffb8e2e3",
	"a6e0bd43-fbfe-4e46-a280-0ed0949dc09c",
	"a91e2d7c-5821-4864-993e-c3dd152b8cb3",
	"ac54ca9d-3a0b-46b1-8d90-2ce07df7ba07",
	"b0066a2a-40c5-4df5-a92f-2a87db4c2566",
	"b1b2e1fd-3e3a-461e-a77e-dc8b4272f84c",
	"b5bacb18-6701-4ad2-8ab5-32a33303bc97",
	"c9c2f562-2607-4000-bc68-8d05bfa80e1b",
	"ce63dfbd-2346-4825-8cd7-8f302aac1627",
	"d0bc5816-dde7-4a8e-a4ec-cabcf4e87d86",
	"d2e47c53-6f1e-46e2-b3e3-4e8112b34bac",
	"d3a931fb-d480-4fd0-9173-f37cbbebcdfb",
	"d5003fb0-740a-4336-98ac-40e7789e7f7b",
	"d941b54f-067a-48f2-b73b-6d91a7b96e1f",
	"ddb2a78c-9086-4fa1-913e-7e7a92cd737b",
	"e3b0e138-3bfa-45de-a719-3970e4c81c1e",
	"e9a1025b-b919-4f44-9f24-ce999a72a82e",
	"ebde3ffe-7a9e-4559-b608-1f3f6c4a984e",
	"ec53ec68-1c98-4823-9423-8ccedf41e0ab",
	"efcc59d7-3049-498a-8f72-655bd3d42465",
	"f26250b4-12ea-4d36-bb4f-af8d89a6a1c2",
	"f4f8dbf8-4db8-4a3a-b96d-528f499e0f04",
	"f534a3b2-6b20-4ba7-8236-7cb2ea195289",
];

export const hasMatchYakness = async (userId?: string | null) => {
	const id = userId ?? (await getCurrentUserId());
	if (!id) return false;
	return sidechatInternal.includes(id) || ambassadorUserIds.includes(id);
};
